export const DEFAULT_MAX_INFLIGHT_REQUESTS = 256;

export interface InflightRequestEntry<TValue> {
  startTime: number;
  value: TValue;
}

export interface BoundedRequestTracker<TValue> {
  track: (requestId: string, value: TValue) => void;
  take: (requestId: string) => InflightRequestEntry<TValue> | undefined;
  size: () => number;
}

export interface BoundedRequestTrackerOptions {
  maxEntries?: number;
  getTimestamp?: () => number;
}

export function createBoundedRequestTracker<TValue>(options: BoundedRequestTrackerOptions = {}): BoundedRequestTracker<TValue> {
  const maxEntries = options.maxEntries || DEFAULT_MAX_INFLIGHT_REQUESTS;
  const getTimestamp = options.getTimestamp || Date.now;
  const inflight = new Map<string, InflightRequestEntry<TValue>>();

  return {
    track: (requestId, value) => {
      if (!requestId) return;
      if (inflight.size >= maxEntries) {
        const oldest = inflight.keys().next().value;
        if (oldest !== undefined) inflight.delete(oldest);
      }
      inflight.set(requestId, {
        startTime: getTimestamp(),
        value,
      });
    },
    take: (requestId) => {
      if (!requestId) return undefined;
      const entry = inflight.get(requestId);
      if (entry) inflight.delete(requestId);
      return entry;
    },
    size: () => inflight.size,
  };
}

export function createWeakMemoizedFactory<TKey extends object, TValue>(factory: (key: TKey) => TValue): (key: TKey) => TValue {
  const cache = new WeakMap<TKey, TValue>();
  return (key) => {
    if (cache.has(key)) {
      return cache.get(key)!;
    }
    const value = factory(key);
    cache.set(key, value);
    return value;
  };
}
